/**
 * ComplexMappings.jsx
 * For defining conditional/polymorphic relationships where target table depends on another column
 * Example: bet_id → Wagers_stp OR Wagers_Saba depending on vendor column value
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { 
  GitBranch, Plus, Trash2, Save, Loader2, ChevronRight, HelpCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const ComplexMappings = ({ database }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    source_column: '',
    condition_column: '',
    description: '',
    mappings: [{ condition_value: '', target_table: '', target_column: '' }]
  });

  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    try {
      const response = await fetch(`${API}/api/learning/complex-mappings`, {
        headers: getAuthHeader()
      }).then(r => r.json());
      setMappings(response.mappings || []);
    } catch (error) {
      console.error('Failed to load:', error);
    } finally {
      setLoading(false);
    }
  };

  const addMappingRow = () => {
    setForm(prev => ({
      ...prev,
      mappings: [...prev.mappings, { condition_value: '', target_table: '', target_column: '' }]
    }));
  };

  const removeMappingRow = (index) => {
    setForm(prev => ({
      ...prev,
      mappings: prev.mappings.filter((_, i) => i !== index)
    }));
  };

  const updateMappingRow = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      mappings: prev.mappings.map((m, i) => i === index ? { ...m, [field]: value } : m)
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.source_column || !form.condition_column) {
      toast.error('Please fill in name, source column, and condition column');
      return;
    }
    
    // Convert mappings array to object
    const mappingsObj = {};
    for (const m of form.mappings) {
      if (m.condition_value && m.target_table) {
        mappingsObj[m.condition_value] = {
          table: m.target_table,
          column: m.target_column || m.source_column
        };
      }
    }
    
    if (Object.keys(mappingsObj).length === 0) {
      toast.error('Please add at least one mapping');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/learning/complex-mapping`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          source_column: form.source_column,
          condition_column: form.condition_column,
          description: form.description,
          mappings: mappingsObj,
          database: database || 'default'
        })
      }).then(r => r.json());
      
      if (response.success) {
        toast.success('Complex mapping saved! AI will use this for queries.');
        setShowForm(false);
        setForm({
          name: '',
          source_column: '',
          condition_column: '',
          description: '',
          mappings: [{ condition_value: '', target_table: '', target_column: '' }]
        });
        loadMappings();
      } else {
        toast.error(response.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete mapping "${name}"?`)) return;
    
    try {
      await fetch(`${API}/api/learning/complex-mapping/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      toast.success('Mapping deleted');
      loadMappings();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-orange-600" />
            Complex/Conditional Mappings
          </div>
          {!showForm && (
            <Button size="sm" onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Add Rule
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Help text */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4 text-sm">
          <div className="flex items-start gap-2">
            <HelpCircle className="w-4 h-4 text-orange-600 mt-0.5" />
            <div>
              <p className="text-orange-800 font-medium">For polymorphic relationships</p>
              <p className="text-orange-600 mt-1">
                Use this when a column maps to different tables based on another column's value.
                <br />
                Example: <code className="bg-orange-100 px-1 rounded">bet_id</code> → 
                <code className="bg-orange-100 px-1 rounded">Wagers_stp</code> when vendor='STP', 
                or <code className="bg-orange-100 px-1 rounded">Wagers_Saba</code> when vendor='Saba'
              </p>
            </div>
          </div>
        </div>

        {/* Existing mappings */}
        {mappings.length > 0 && (
          <div className="space-y-3 mb-4">
            {mappings.map((m) => (
              <div key={m.id} className="border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-800">{m.name}</span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => handleDelete(m.id, m.name)}
                    className="text-red-500 h-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  <code className="bg-gray-200 px-1 rounded">{m.source_column}</code> target depends on 
                  <code className="bg-gray-200 px-1 rounded ml-1">{m.condition_column}</code>
                </p>
                <div className="space-y-1 text-sm">
                  {Object.entries(m.mappings || {}).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2 text-gray-600">
                      <span>If {m.condition_column} = "{key}"</span>
                      <ChevronRight className="w-3 h-3" />
                      <span className="font-medium">{val.table}.{val.column}</span>
                    </div>
                  ))}
                </div>
                {m.description && (
                  <p className="text-xs text-gray-500 mt-2 italic">{m.description}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add form */}
        {showForm && (
          <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50/50">
            <h4 className="font-medium text-gray-800 mb-3">New Complex Mapping</h4>
            
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-sm text-gray-600">Rule Name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., bet_id polymorphic"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">Source Column</label>
                <Input
                  value={form.source_column}
                  onChange={(e) => setForm(prev => ({ ...prev, source_column: e.target.value }))}
                  placeholder="e.g., bet_id"
                />
              </div>
            </div>
            
            <div className="mb-3">
              <label className="text-sm text-gray-600">Condition Column (determines which table to use)</label>
              <Input
                value={form.condition_column}
                onChange={(e) => setForm(prev => ({ ...prev, condition_column: e.target.value }))}
                placeholder="e.g., vendor, platform"
              />
            </div>
            
            <div className="mb-3">
              <label className="text-sm text-gray-600 mb-2 block">Mappings</label>
              <div className="space-y-2">
                {form.mappings.map((m, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 w-8">If</span>
                    <Input
                      value={m.condition_value}
                      onChange={(e) => updateMappingRow(idx, 'condition_value', e.target.value)}
                      placeholder="value (e.g., STP)"
                      className="w-32"
                    />
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                    <Input
                      value={m.target_table}
                      onChange={(e) => updateMappingRow(idx, 'target_table', e.target.value)}
                      placeholder="target table"
                      className="flex-1"
                    />
                    <span className="text-gray-400">.</span>
                    <Input
                      value={m.target_column}
                      onChange={(e) => updateMappingRow(idx, 'target_column', e.target.value)}
                      placeholder="column"
                      className="w-32"
                    />
                    {form.mappings.length > 1 && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => removeMappingRow(idx)}
                        className="text-red-500 h-8 px-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={addMappingRow}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Condition
                </Button>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-sm text-gray-600">Description (optional - explain the logic to AI)</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Explain how this mapping works in plain English..."
                className="w-full h-20 p-2 border rounded-lg text-sm"
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Mapping
              </Button>
            </div>
          </div>
        )}

        {mappings.length === 0 && !showForm && (
          <p className="text-center text-gray-500 py-4">
            No complex mappings defined yet. Click "Add Rule" to create one.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ComplexMappings;
